import _ from 'lodash';
import { deepFreeze } from 'shared/util';

import GameData from 'shared/gameData';

const BASE_COLUMN_WIDTH = 600;

let traitEntryLookup;
const traitEntryIds = new Set();
const getTraitEntry = (specId, entryId) => {
  if (!traitEntryLookup) {
    traitEntryLookup = {};
    _.each(GameData.NewTalents, tree => {
      _.each([tree.specNodes, tree.classNodes], (nodes, idx) => {
        const whichTree = idx === 0 ? 'spec' : 'class';
        _.each(nodes, node => {
          _.each(node.entries, entry => {
            if (!traitEntryLookup[tree.specId]) {
              traitEntryLookup[tree.specId] = {};
            }
            traitEntryLookup[tree.specId][entry.id] = {
              ...entry,
              nodeId: node.id,
              node,
              tree: whichTree,
              treeId: tree.traitTreeId,
              className: tree.className,
              classId: tree.classId,
              specName: tree.specName,
              specId: tree.specId,
            };
            traitEntryIds.add(entry.id);
          });
        });
      });
    });
    deepFreeze(traitEntryLookup);
  }
  if (traitEntryLookup[specId]) {
    return traitEntryLookup[specId][entryId];
  }
  return null;
};

let traitNodeLookup;
const getTraitNode = nodeId => {
  if (!traitNodeLookup) {
    traitNodeLookup = {};
    _.each(GameData.NewTalents, tree => {
      _.each([tree.specNodes, tree.classNodes], (nodes, idx) => {
        const whichTree = idx === 0 ? 'spec' : 'class';
        _.each(nodes, node => {
          traitNodeLookup[node.id] = {
            ...node,
            tree: whichTree,
            treeId: tree.traitTreeId,
            className: tree.className,
            classId: tree.classId,
            specName: tree.specName,
            specId: tree.specId,
          };
        });
      });
    });
    deepFreeze(traitNodeLookup);
  }
  return traitNodeLookup[nodeId];
};

const getTreeBySpecId = _.memoize((specId) => {
  return _.find(GameData.NewTalents, { specId });
});
const getTreeByTreeId = _.memoize((traitTreeId) => {
  return _.find(GameData.NewTalents, { traitTreeId });
});

// gets all nodes for a spec and adjusts the X position to shrink the empty space between class/spec trees
const getDisplayAdjustedNodes = (specId) => {
  const tree = getTreeBySpecId(specId);

  const classNodes = _.cloneDeep(tree.classNodes);
  let specNodes = _.cloneDeep(tree.specNodes);

  const classRightSide = _.max(_.map(classNodes, 'posX'));
  const specLeftSide = _.min(_.map(specNodes, 'posX'));
  const offset = classRightSide - specLeftSide;
  specNodes = _.map(specNodes, node => ({
    ...node,
    posX: node.posX + offset + (2 * BASE_COLUMN_WIDTH),
  }));

  return {
    classNodes,
    specNodes,
  };
};

const getBounds = nodes => {
  let xMin = Infinity;
  let xMax = -Infinity;
  let yMin = Infinity;
  let yMax = -Infinity;

  _.each(nodes, node => {
    if (node.posX < xMin) xMin = node.posX;
    if (node.posX > xMax) xMax = node.posX;
    if (node.posY < yMin) yMin = node.posY;
    if (node.posY > yMax) yMax = node.posY;
  });
  const xRange = xMax - xMin;

  const yRange = yMax - yMin;

  const numColumns = xRange / BASE_COLUMN_WIDTH;

  return {
    xMin,
    xMax,
    xRange,

    yMin,
    yMax,
    yRange,

    numColumns,
  };
};

const splitTalents = str => {
  const bits = _.filter(_.split(str, '/'));
  const entries = _.map(bits, s => {
    const [entryId, rank] = _.map(_.split(s, ':'), i => parseInt(i, 10));
    return {
      entryId,
      rank,
    };
  });
  return entries;
};

const base64Char = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const base64CharValue = _.fromPairs(_.map(base64Char, (char, index) => [char, index]));

const BYTE_SIZE = 6;
const VERSION_BITS = 8;
const SPEC_BITS = 16;
const TREE_BITS = 128;
const RANK_BITS = 6;
const CHOICE_BITS = 2;

const looksLikeBlizzard = str => {
  if (!str) {
    return false;
  }
  return _.every(str, ch => _.has(base64CharValue, ch));
};

const parseBlizzardTalents = str => {
  if (!str) {
    return '';
  }

  let head = 0;
  const getBits = bits => {
    let value = 0;
    for (let i = 0; i < bits; i += 1) {
      const pos = Math.floor(head / BYTE_SIZE);
      const char = str[pos];
      const byte = base64CharValue[char];
      const bit = head % BYTE_SIZE;
      head += 1;
      // eslint-disable-next-line no-bitwise
      value += ((byte >> bit) & 0b1) << i;
    }
    return value;
  };

  // eslint-disable-next-line no-unused-vars
  const version = getBits(VERSION_BITS);
  const specId = getBits(SPEC_BITS);
  // eslint-disable-next-line no-unused-vars
  const treeHash = getBits(TREE_BITS);

  const treeData = getTreeBySpecId(specId);

  if (!treeData) {
    return '';
  }

  const entries = [];
  const unknownNodes = [];

  for (let i = 0; i < treeData.fullNodeOrder.length; i += 1) {
    const selected = getBits(1);
    const nodeId = treeData.fullNodeOrder[i];
    const node = getTraitNode(nodeId);

    if (selected) {
      if (node) {
        let rank = node.maxRanks;
        let choiceEntryIndex = 0;

        const isRanked = getBits(1);
        if (isRanked) {
          rank = getBits(RANK_BITS);
        }

        const isChoiceNode = getBits(1);
        if (isChoiceNode) {
          choiceEntryIndex = getBits(CHOICE_BITS);
        }

        const entry = node.entries[choiceEntryIndex];
        if (entry) {
          entries.push({
            id: entry.id,
            rank,
          });
        } else {
          unknownNodes.push(nodeId);
        }
      } else {
        unknownNodes.push(nodeId);
      }
    }
  }

  return {
    version,
    specId,
    treeHash,
    entries,
    unknownNodes,
  };
};

const parse = (str, incSpecId = null) => {
  let specId = incSpecId;

  if (!str && !specId) {
    // TODO: Throw an error and clean up all the resulting mess
    return null;
  }

  // already parsed
  if (str?.specId && str?.entries) {
    return str;
  }

  // pre-dragonflight talent string, not handled so just return null
  if (_.isString(str) && str.length === 7) {
    return null;
  }

  // older URL talent format that SimC somewhat supports
  if (_.startsWith(str, 'http:') || _.startsWith(str, 'https:')) {
    return null;
  }

  const spent = {
    spec: 0,
    class: 0,
  };

  let treeData;
  const treeFields = ['treeId', 'className', 'classId', 'specName', 'specId', 'fullNodeOrder'];

  let entriesStr = str;
  let unknownEntries = [];

  // if it looks like a base64 string from in-game, convert the base64 into a talents string
  if (looksLikeBlizzard(str)) {
    const talentInfo = parseBlizzardTalents(str);
    if (!talentInfo) {
      return null;
    }
    entriesStr = _.join(
      _.map(talentInfo.entries, entry => _.join([entry.id, entry.rank], ':')),
      '/',
    );
    treeData = getTreeBySpecId(talentInfo.specId);
    unknownEntries = talentInfo.unknownNodes;
    specId = talentInfo.specId;
  }

  const rawEntries = splitTalents(entriesStr);
  const entries = _.filter(_.map(rawEntries, ({ entryId, rank }) => {
    // find entry
    const entry = getTraitEntry(specId, entryId);
    if (!entry) {
      unknownEntries.push(entryId);
      return null;
    }
    if (entry.node.freeNode) {
      // skip free nodes since these are not an explicit choice
      // this simplifies some other stuff in the code (counting spent points without needing to know all node info)
      // I sort of have a bad feeling about this
      return null;
    }
    if (!treeData && entry.tree === 'spec') {
      treeData = _.pick(getTreeBySpecId(entry.specId), treeFields);
    }
    spent[entry.tree] += rank;
    return {
      entry: _.omit(entry, treeFields),
      rank,
    };
  }));
  if (!treeData && specId) {
    const tree = getTreeBySpecId(specId);
    treeData = _.pick(tree, treeFields);
  }

  const result = {
    ...treeData,
    spent,
    entries: _.sortBy(entries, ['entry.node.posY', 'entry.node.posX']),
    rawString: entriesStr,
    rawInput: str,
    unknownEntries,
  };

  return result;
};

const stringifyBlizzardV1 = talents => {
  if (!talents) {
    return '';
  }
  if (!talents.specId) {
    return '';
  }

  let str = '';
  let head = 0;
  let byte = 0;

  const putBits = (bits, value) => {
    for (let i = 0; i < bits; i += 1) {
      const bit = head % BYTE_SIZE;
      // eslint-disable-next-line no-bitwise
      byte += ((value >> i) & 0b1) << bit;
      if (bit === BYTE_SIZE - 1) {
        str += base64Char[byte];
        byte = 0;
      }
      head += 1;
    }
  };
  const finish = () => {
    if (head % BYTE_SIZE) {
      str += base64Char[byte];
    }
  };

  const tree = getTreeBySpecId(talents.specId);

  const entryLookup = _.keyBy(talents.entries, 'entry.nodeId');

  putBits(VERSION_BITS, 1);
  putBits(SPEC_BITS, talents.specId);
  putBits(TREE_BITS, 0); // 0-filled to bypass in-game validation

  for (let i = 0; i < tree.fullNodeOrder.length; i += 1) {
    const node = getTraitNode(tree.fullNodeOrder[i]);
    const entry = entryLookup[node?.id];
    const entryInfo = getTraitEntry(talents.specId, entry?.entry?.id);
    const selected = Boolean(entry);
    putBits(1, selected ? 1 : 0);
    if (entry) {
      const isPartialRanked = entry.rank !== entryInfo?.maxRanks;
      putBits(1, isPartialRanked ? 1 : 0);
      if (isPartialRanked) {
        putBits(RANK_BITS, entry.rank);
      }

      const isChoiceNode = node.type === 'choice';
      putBits(1, isChoiceNode ? 1 : 0);
      if (isChoiceNode) {
        const index = _.findIndex(node.entries, { id: entry.entry.id });
        if (index < 0) {
          throw new Error('Unable to find choice entry which is quite unexpected');
        }
        putBits(CHOICE_BITS, index);
      }
    }
  }

  finish();

  return str;
};

const stringifySimcClassTalents = talents => {
  return _.map(talents.entries, t => _.join([t.entry.id, t.rank], ':')).join('/');
};

const stringify = (talents, format = 'blizzard') => {
  if (_.isEmpty(talents)) {
    return null;
  }
  switch (format) {
    case 'blizzardV1':
    case 'blizzard':
      return stringifyBlizzardV1(talents);
    case 'simc_trait_entry':
      return stringifySimcClassTalents(talents);
    default:
      throw new Error(`TalentTraits.stringify: Unknown format ${format}`);
  }
};

const normalize = str => stringify(parse(str));

const getPointsForLevel = level => {
  const base = level - 10;
  return {
    class: _.floor(base / 2) + 1,
    spec: _.ceil(base / 2),
  };
};

const diff = (a, b) => {
  let aParsed = a;
  let bParsed = b;

  if (_.isString(a)) {
    aParsed = parse(a);
  }
  if (_.isString(b)) {
    bParsed = parse(b);
  }

  if (!aParsed || !bParsed) {
    return null;
  }

  const collect = {};
  const entries = {};
  const iMap = ['a', 'b'];
  _.each([aParsed, bParsed], (talents, i) => {
    _.each(talents.entries, entry => {
      const key = _.join([entry.entry.id, entry.rank], '-');
      if (!collect[key]) {
        collect[key] = [];
      }
      collect[key].push(iMap[i]);
      entries[key] = entry;
    });
  });

  const shared = [];
  const onlyA = [];
  const onlyB = [];

  _.each(collect, (inWhich, key) => {
    const entry = entries[key];
    let which;
    if (collect[key].length === 2) {
      which = shared;
    } else if (_.first(collect[key]) === 'a') {
      which = onlyA;
    } else {
      which = onlyB;
    }
    which.push(entry);
  });

  return {
    shared,
    onlyA,
    onlyB,
    a: aParsed,
    b: bParsed,
  };
};

export default {
  BASE_COLUMN_WIDTH,

  parse,
  stringify,
  normalize,

  diff,

  getTreeBySpecId,
  getTreeByTreeId,

  getTraitNode,

  getDisplayAdjustedNodes,
  getBounds,

  getPointsForLevel,
};
